// ==================== Enums ====================

export const ConnectionType = {
  Direct: 'Direct',
  Team: 'Team',
  Auto: 'Auto',
  Manual: 'Manual',
} as const;

export type ConnectionTypeValue = (typeof ConnectionType)[keyof typeof ConnectionType];

export const ContactReferenceType = {
  Individual: 'Individual',
  Entity: 'Entity',
  External: 'External',
  Organization: 'Organization',
} as const;

export type ContactReferenceTypeValue =
  (typeof ContactReferenceType)[keyof typeof ContactReferenceType];

export const CategorySelectType = {
  Single: 'Single',
  Multi: 'Multi',
} as const;

export type CategorySelectTypeValue = (typeof CategorySelectType)[keyof typeof CategorySelectType];

export const CategoryNodeKind = {
  Options: 'Options',
  Field: 'Field',
} as const;

export type CategoryNodeKindValue = (typeof CategoryNodeKind)[keyof typeof CategoryNodeKind];

export const CategoryValueType = {
  Date: 'Date',
} as const;

export type CategoryValueTypeValue = (typeof CategoryValueType)[keyof typeof CategoryValueType];

export const ContactOrderBy = {
  Name: 'Name',
  JobTitle: 'JobTitle',
  Affiliation: 'Affiliation',
  Type: 'Type',
  ReferenceType: 'ReferenceType',
  LastInteraction: 'LastInteraction',
  CreatedAt: 'CreatedAt',
  LastEdited: 'LastEdited',
  FollowUp: 'FollowUp',
} as const;

export type ContactOrderByValue = (typeof ContactOrderBy)[keyof typeof ContactOrderBy];

export const OrderDirection = {
  Asc: 'Asc',
  Desc: 'Desc',
} as const;

export type OrderDirectionValue = (typeof OrderDirection)[keyof typeof OrderDirection];

export const ContactFieldType = {
  Email: 'Email',
  Phone: 'Phone',
  Address: 'Address',
  Website: 'Website',
  Social: 'Social',
} as const;

export type ContactFieldTypeValue = (typeof ContactFieldType)[keyof typeof ContactFieldType];

export const ContactValueSource = {
  System: 'System',
  User: 'User',
} as const;

export type ContactValueSourceValue = (typeof ContactValueSource)[keyof typeof ContactValueSource];

// ==================== Shared ====================

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface IdRes {
  id: string;
}

export interface SuccessRes {
  success: boolean;
}

// ==================== Contact types ====================

export interface CategoryPathNode {
  id: string;
  name: string;
}

export interface ContactCategoryPath {
  nodeId: string;
  nodeName: string;
  rootName: string;
  path: CategoryPathNode[];
}

export interface ContactCategoryFieldValue {
  nodeId: string;
  value: string;
}

export interface ContactFieldValue {
  fieldType: ContactFieldTypeValue;
  label: string | null;
  value: string;
  source: ContactValueSourceValue;
  editable: boolean;
  position: number;
}

export interface ContactFieldAddressInput {
  country: string | null;
  city: string | null;
  street: string | null;
  street2?: string | null;
  state?: string | null;
  postalCode?: string | null;
}

export type ContactFieldValueInput =
  | {
      fieldType: Exclude<ContactFieldTypeValue, (typeof ContactFieldType)['Address']>;
      label?: string | null;
      value: string;
    }
  | {
      fieldType: (typeof ContactFieldType)['Address'];
      label?: string | null;
      address: ContactFieldAddressInput;
    };

export interface OrganizationAffiliation {
  id: string;
  name: string | null;
  iconId: string | null;
  referenceType: ContactReferenceTypeValue;
  jobTitle: string | null;
}
export const ContactFollowUpState = {
  Upcoming: 'Upcoming',
  DueToday: 'DueToday',
  Overdue: 'Overdue',
} as const;

export type ContactFollowUpStateValue =
  (typeof ContactFollowUpState)[keyof typeof ContactFollowUpState];

export const ContactFollowUpRangePreset = {
  Today: 'Today',
  ThisWeek: 'ThisWeek',
  NextMonth: 'NextMonth',
  Overdue: 'Overdue',
} as const;

export type ContactFollowUpRangePresetValue =
  (typeof ContactFollowUpRangePreset)[keyof typeof ContactFollowUpRangePreset];

/**
 * The signed-in user's own reminder on a contact. `state` and `daysOverdue` are resolved by the
 * server in the zone captured on the reminder — never recompute "today" from `dueAt` locally, or
 * the highlight will disagree with the filter and the digest.
 */
export interface ContactFollowUp {
  id: string;
  dueAt: string;
  timeSpecified: boolean;
  timeZone: string;
  state: ContactFollowUpStateValue;
  daysOverdue: number;
}

export interface SetContactFollowUpPayload {
  date: string;
  time?: string | null;
  timeZone: string;
}

export interface ContactBase {
  id: string;
  ownerType: string;
  ownerId: string;
  type: ConnectionTypeValue;
  referenceType: ContactReferenceTypeValue;
  referenceId: string | null;
  email: string | null;
  jobTitle: string | null;
  /** @deprecated Migrated into `values`. */
  socialLinks: string[] | null;
  /** @deprecated Migrated into `values`. */
  country: string | null;
  /** @deprecated Migrated into `values`. */
  city: string | null;
  /** @deprecated Migrated into `values`. */
  address: string | null;
  lastInteraction: string | null;
  affiliation: string | null;
  name: string | null;
  notes: string | null;
  /** @deprecated Migrated into `values`. */
  phone: string | null;
  company: string | null;
  /** @deprecated Migrated into `values`. */
  website: string | null;
  categoryNodeIds: string[];
  categories: ContactCategoryPath[];
  values: ContactFieldValue[];
  createdAt: string;
  updatedAt: string;
  /** Last user-facing edit; system-synced changes don't advance it. Set to creation time initially. */
  lastEditedAt: string;
  /** The CALLER's own follow-up, or null when they have none. Other users' reminders never appear. */
  followUp?: ContactFollowUp | null;
}

export interface Contact extends ContactBase {
  iconId: string | null;
  organizationAffiliations?: OrganizationAffiliation[];
}

export interface CreateContactPayload {
  type: ConnectionTypeValue;
  referenceType: ContactReferenceTypeValue;
  referenceId?: string | null;
  email?: string | null;
  iconId?: string | null;
  name?: string | null;
  jobTitle?: string | null;
  notes?: string | null;
  phone?: string | null;
  company?: string | null;
  website?: string | null;
  socialLinks?: string[] | null;
  country?: string | null;
  city?: string | null;
  address?: string | null;
  categoryNodeIds?: string[];
  organizationId?: string | null;
  contactCode?: string | null;
  values?: ContactFieldValueInput[];
}

export interface CreateContactResponse {
  id: string;
  isNew: boolean;
}

export interface UpdateContactPayload {
  name?: string | null;
  email?: string | null;
  iconId?: string | null;
  jobTitle?: string | null;
  notes?: string | null;
  phone?: string | null;
  company?: string | null;
  website?: string | null;
  socialLinks?: string[] | null;
  country?: string | null;
  city?: string | null;
  address?: string | null;
  values?: ContactFieldValueInput[];
  ownerAffiliations?: string[];
}

export interface ContactOwnerAffiliationsRes {
  affiliations: string[];
}

export interface UpdateContactCategoriesPayload {
  categoryNodeIds: string[];
}

export interface GetLinkedContactsParams {
  page?: number;
  limit?: number;
}

export type LinkedContact = Contact & { linkJobTitle: string | null };

export interface GetLinkedContactsRes {
  contacts: LinkedContact[];
  pagination: PaginationMeta;
}

export interface LinkOrganizationPayload {
  organizationId: string;
  jobTitle?: string | null;
}

export interface UpdateOrganizationLinkPayload {
  jobTitle: string | null;
}

// ==================== Category types ====================

export interface CategoryNode {
  id: string;
  ownerType: string;
  ownerId: string;
  parentNodeId: string | null;
  name: string;
  selectType: CategorySelectTypeValue;
  nodeKind: CategoryNodeKindValue;
  valueType: CategoryValueTypeValue | null;
  sortOrder: number;
  locked: boolean;
  childrenCount?: number;
  contactCount?: number;
  createdAt: string;
  updatedAt: string;
  children: CategoryNode[];
}

export interface CreateCategoryNodePayload {
  parentNodeId?: string;
  name: string;
  selectType: CategorySelectTypeValue;
  nodeKind?: CategoryNodeKindValue;
  valueType?: CategoryValueTypeValue;
}

export interface UpdateCategoryNodePayload {
  name?: string;
  selectType?: CategorySelectTypeValue;
}

export interface BatchSaveCategoryNodePayload {
  id?: string | null;
  name: string;
  selectType: CategorySelectTypeValue;
  children: BatchSaveCategoryNodePayload[];
}

export interface MergeCategoryNodesPayload {
  sourceNodeIds: string[];
  targetNodeId: string;
}

// ==================== Contact Upload types ====================

export interface CreateContactUploadRequestPayload {
  filename: string;
  contentType: string;
  contentEncoding?: string;
  size: number;
}

export interface CreateContactUploadRequestRes {
  fileId: string;
  visibility: FileVisibilityValue;
  uploadUrl: string;
  uploadHeaders: Record<string, string>;
  expiresAt: string;
}

// ==================== Contact Extractor types ====================

export const FileVisibility = {
  Private: 'private',
  Public: 'public',
} as const;

export type FileVisibilityValue = (typeof FileVisibility)[keyof typeof FileVisibility];

export interface CreateExtractorUploadRequestPayload {
  filename: string;
  contentType: string;
  size: number;
}

export interface CreateExtractorUploadRequestRes {
  fileId: string;
  visibility: FileVisibilityValue;
  uploadUrl: string;
  uploadHeaders: Record<string, string>;
  expiresAt: string;
}

export interface ExtractContactsPayload {
  fileId: string;
}

export interface ExtractContactsRes {
  email: string | null;
  fullName: string | null;
  jobTitle: string | null;
  phone: string | null;
  company: string | null;
  website: string | null;
  socialLinks: string[] | null;
  country: string | null;
  city: string | null;
  address: string | null;
}

export const ContactHistoryKind = {
  Call: 'Call',
  Message: 'Message',
  Meeting: 'Meeting',
  Note: 'Note',
  FieldChanged: 'FieldChanged',
  NoteChanged: 'NoteChanged',
  CategoryChanged: 'CategoryChanged',
} as const;

export type ContactHistoryKindValue = (typeof ContactHistoryKind)[keyof typeof ContactHistoryKind];

export const CONTACT_HISTORY_COMMUNICATION_KINDS = [
  ContactHistoryKind.Call,
  ContactHistoryKind.Message,
  ContactHistoryKind.Meeting,
  ContactHistoryKind.Note,
] as const;

export type ContactHistoryCommunicationKind = (typeof CONTACT_HISTORY_COMMUNICATION_KINDS)[number];

export const CONTACT_HISTORY_SYSTEM_KINDS = [
  ContactHistoryKind.FieldChanged,
  ContactHistoryKind.NoteChanged,
  ContactHistoryKind.CategoryChanged,
] as const;

export const CONTACT_HISTORY_CONTENT_MAX_LENGTH = 5000;
export const CONTACT_HISTORY_MAX_ATTACHMENTS = 10;
export const CONTACT_FILE_MAX_BYTES = 25 * 1024 * 1024;
export const CONTACT_FILE_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  'text/plain',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
] as const;
export const CONTACT_FILE_ALLOWED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.csv',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.gif',
  '.heic',
] as const;

export interface ContactHistoryCommunicationPayload {
  text: string | null;
  fileIds: string[];
}

export interface ContactHistoryFieldChangedPayload {
  field: string;
  label?: string;
  to: string | string[] | null;
}

export interface ContactHistoryNoteChangedPayload {
  to: string | null;
}

export interface ContactHistoryCategoryChangedPayload {
  rootName: string;
  nodeName?: string;
  to: string | string[] | null;
}

export type ContactHistoryPayload =
  | ContactHistoryCommunicationPayload
  | ContactHistoryFieldChangedPayload
  | ContactHistoryNoteChangedPayload
  | ContactHistoryCategoryChangedPayload;

export interface ContactHistoryEntryDto {
  id: string;
  contactId: string;
  kind: ContactHistoryKindValue;
  actorId: string | null;
  createdAt: string;
  updatedAt: string;
  payload: ContactHistoryPayload;
}

export interface AddContactHistoryEntryPayload {
  kind: ContactHistoryCommunicationKind;
  text?: string;
  fileIds?: string[];
}

export interface UpdateContactHistoryEntryPayload {
  text?: string;
  fileIds?: string[];
}

export interface CreateContactFileUploadRequestPayload {
  filename: string;
  contentType: string;
  size: number;
}
